import { useState, useMemo, useCallback } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Alert,
  AlertTitle,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  SelectChangeEvent,
  Skeleton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { trpc } from '../../lib/trpc';
import { MonitorRow, MonitorRowSkeleton } from '../../components/monitors/MonitorRow';
import type { MonitorStatus } from '../../components/ui/StatusBadge';

/**
 * Items per page
 */
const PAGE_SIZE = 20;

/**
 * Sort options
 */
type SortOption = 'name' | 'status' | 'uptime' | 'created';
const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'status', label: 'Status' },
  { value: 'uptime', label: 'Uptime' },
  { value: 'created', label: 'Recently Added' },
];

/**
 * Status filter options
 */
type StatusFilter = 'ALL' | MonitorStatus;
const statusFilters: { value: StatusFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'UP', label: 'Up' },
  { value: 'DOWN', label: 'Down' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'PENDING', label: 'Pending' },
];

/**
 * Summary stat card component
 */
interface SummaryStatProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  isLoading?: boolean;
}

function SummaryStat({ label, value, icon, color, isLoading }: SummaryStatProps) {
  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        height: '100%',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: `${color}15`,
          color: color,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          {label}
        </Typography>
        {isLoading ? (
          <Skeleton variant="text" width={40} height={28} />
        ) : (
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {value}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

/**
 * Summary stats skeleton
 */
function SummaryStatsSkeleton() {
  return (
    <Grid container spacing={1.5}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Grid item xs={6} sm={4} md={2.4} key={i}>
          <Skeleton variant="rounded" height={72} />
        </Grid>
      ))}
    </Grid>
  );
}

/**
 * Table header component
 */
function TableHeader() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1.5, sm: 2 },
        px: 2,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'action.hover',
      }}
    >
      {/* Status */}
      <Box sx={{ width: 10 }} />

      {/* Name */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          minWidth: { xs: 80, sm: 120 },
        }}
      >
        Name
      </Typography>

      {/* Spacer */}
      <Box sx={{ flex: 1, minWidth: 0 }} />

      {/* Timeline */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          display: { xs: 'none', md: 'block' },
          width: 96,
          textAlign: 'center',
        }}
      >
        Last 24h
      </Typography>

      {/* Uptime */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          width: 52,
          textAlign: 'right',
        }}
      >
        Uptime
      </Typography>

      {/* Response time */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          width: 56,
          textAlign: 'right',
          display: { xs: 'none', sm: 'block' },
        }}
      >
        Resp
      </Typography>

      {/* Last check */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          width: 32,
          textAlign: 'right',
          display: { xs: 'none', sm: 'block' },
        }}
      >
        Last
      </Typography>

      {/* Actions placeholder */}
      <Box sx={{ width: 36, ml: 0.5 }} />
    </Box>
  );
}

/**
 * Monitors page component
 * Main landing page showing monitor summary and list
 */
export function MonitorsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter state from URL params
  const statusFilter = (searchParams.get('status') as StatusFilter) || 'ALL';
  const searchQuery = searchParams.get('search') || '';
  const sortBy = (searchParams.get('sort') as SortOption) || 'created';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // Local search state (for debouncing)
  const [localSearch, setLocalSearch] = useState(searchQuery);

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [monitorToDelete, setMonitorToDelete] = useState<string | null>(null);

  // Fetch monitors
  const {
    data: monitors,
    isLoading,
    error,
    refetch,
    isFetching,
  } = trpc.monitors.list.useQuery(undefined, {
    refetchInterval: 30000,
  });

  // Mutations
  const pauseMutation = trpc.monitors.pause.useMutation({
    onSuccess: () => refetch(),
  });
  const resumeMutation = trpc.monitors.resume.useMutation({
    onSuccess: () => refetch(),
  });
  const deleteMutation = trpc.monitors.delete.useMutation({
    onSuccess: () => {
      refetch();
      setDeleteDialogOpen(false);
      setMonitorToDelete(null);
    },
  });

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!monitors) return null;

    const total = monitors.length;
    const up = monitors.filter((m) => m.status === 'UP').length;
    const down = monitors.filter((m) => m.status === 'DOWN').length;
    const paused = monitors.filter((m) => m.status === 'PAUSED').length;

    // Calculate 24h uptime from active monitors
    const activeMonitors = monitors.filter((m) => m.status !== 'PAUSED');
    const uptime24h =
      activeMonitors.length > 0
        ? activeMonitors.reduce((acc, m) => acc + m.uptimePercentage, 0) / activeMonitors.length
        : null;

    return { total, up, down, paused, uptime24h };
  }, [monitors]);

  // Update URL params
  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (
          value === null ||
          value === '' ||
          value === 'ALL' ||
          (key === 'page' && value === '1')
        ) {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Filter and sort monitors
  const filteredMonitors = useMemo(() => {
    if (!monitors) return [];

    let result = [...monitors];

    // Apply status filter
    if (statusFilter !== 'ALL') {
      result = result.filter((m) => m.status === statusFilter);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (m) => m.name.toLowerCase().includes(query) || m.url.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'status': {
        const statusOrder = { DOWN: 0, PENDING: 1, UP: 2, PAUSED: 3 };
        result.sort(
          (a, b) =>
            statusOrder[a.status as keyof typeof statusOrder] -
            statusOrder[b.status as keyof typeof statusOrder]
        );
        break;
      }
      case 'uptime':
        result.sort((a, b) => a.uptimePercentage - b.uptimePercentage);
        break;
      case 'created':
      default:
        break;
    }

    return result;
  }, [monitors, statusFilter, searchQuery, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredMonitors.length / PAGE_SIZE);
  const paginatedMonitors = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredMonitors.slice(start, start + PAGE_SIZE);
  }, [filteredMonitors, currentPage]);

  // Status counts for filter badges
  const statusCounts = useMemo(() => {
    if (!monitors) return {};
    return monitors.reduce(
      (acc, m) => {
        acc[m.status] = (acc[m.status] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [monitors]);

  // Handlers
  const handleStatusFilter = (_: React.MouseEvent<HTMLElement>, newStatus: StatusFilter | null) => {
    if (newStatus !== null) {
      updateParams({ status: newStatus, page: '1' });
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearch(event.target.value);
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    updateParams({ search: localSearch, page: '1' });
  };

  const handleSearchKeyUp = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      updateParams({ search: localSearch, page: '1' });
    }
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    updateParams({ sort: event.target.value, page: '1' });
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    updateParams({ page: page.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePause = (id: string) => {
    pauseMutation.mutate({ id });
  };

  const handleResume = (id: string) => {
    resumeMutation.mutate({ id });
  };

  const handleDeleteClick = (id: string) => {
    setMonitorToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (monitorToDelete) {
      deleteMutation.mutate({ id: monitorToDelete });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setMonitorToDelete(null);
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2.5,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Monitors
        </Typography>
        <Button
          component={RouterLink}
          to="/monitors/new"
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
        >
          Add Monitor
        </Button>
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          <AlertTitle>Error loading monitors</AlertTitle>
          {error.message}
        </Alert>
      )}

      {/* Summary Stats */}
      <Box sx={{ mb: 2.5 }}>
        {isLoading ? (
          <SummaryStatsSkeleton />
        ) : (
          <Grid container spacing={1.5}>
            <Grid item xs={6} sm={4} md={2.4}>
              <SummaryStat
                label="Total"
                value={summaryStats?.total ?? 0}
                icon={<MonitorHeartIcon fontSize="small" />}
                color="#3B82F6"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <SummaryStat
                label="Up"
                value={summaryStats?.up ?? 0}
                icon={<CheckCircleIcon fontSize="small" />}
                color="#10B981"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <SummaryStat
                label="Down"
                value={summaryStats?.down ?? 0}
                icon={<ErrorIcon fontSize="small" />}
                color="#EF4444"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <SummaryStat
                label="Paused"
                value={summaryStats?.paused ?? 0}
                icon={<PauseCircleIcon fontSize="small" />}
                color="#F59E0B"
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2.4}>
              <SummaryStat
                label="Avg Uptime"
                value={
                  summaryStats?.uptime24h !== null
                    ? `${summaryStats?.uptime24h.toFixed(1)}%`
                    : '-'
                }
                icon={<TrendingUpIcon fontSize="small" />}
                color="#8B5CF6"
              />
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Filters and Controls */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 1.5,
            alignItems: { xs: 'stretch', md: 'center' },
          }}
        >
          {/* Search */}
          <Box
            component="form"
            onSubmit={handleSearchSubmit}
            sx={{ flex: 1, minWidth: { xs: '100%', md: 180 } }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search monitors..."
              value={localSearch}
              onChange={handleSearchChange}
              onKeyUp={handleSearchKeyUp}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiInputBase-input': { py: 0.75 } }}
            />
          </Box>

          {/* Status Filter */}
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={handleStatusFilter}
            size="small"
            sx={{
              flexWrap: 'wrap',
              '& .MuiToggleButton-root': {
                px: { xs: 1, sm: 1.5 },
                py: 0.5,
              },
            }}
          >
            {statusFilters.map((filter) => (
              <ToggleButton key={filter.value} value={filter.value}>
                {filter.label}
                {filter.value !== 'ALL' && (statusCounts[filter.value] ?? 0) > 0 && (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.5,
                      px: 0.5,
                      py: 0.125,
                      borderRadius: 0.75,
                      fontSize: '0.7rem',
                      backgroundColor:
                        statusFilter === filter.value ? 'primary.main' : 'action.selected',
                      color:
                        statusFilter === filter.value ? 'primary.contrastText' : 'text.secondary',
                    }}
                  >
                    {statusCounts[filter.value]}
                  </Box>
                )}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {/* Sort */}
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={sortBy}
              onChange={handleSortChange}
              label="Sort by"
              sx={{ '& .MuiSelect-select': { py: 0.75 } }}
            >
              {sortOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Refresh */}
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => refetch()}
              disabled={isFetching}
              size="small"
              sx={{ border: 1, borderColor: 'divider' }}
            >
              <RefreshIcon
                fontSize="small"
                sx={{
                  animation: isFetching ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Content */}
      {isLoading ? (
        <Paper sx={{ overflow: 'hidden' }}>
          <TableHeader />
          {Array.from({ length: 8 }).map((_, i) => (
            <MonitorRowSkeleton key={i} />
          ))}
        </Paper>
      ) : filteredMonitors.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <MonitorHeartIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
          {monitors && monitors.length > 0 ? (
            <>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
                No monitors match your filters
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mb: 2.5 }}>
                Try adjusting your search or filter criteria.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setLocalSearch('');
                  updateParams({ status: null, search: null, sort: null, page: null });
                }}
              >
                Clear Filters
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
                No monitors yet
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mb: 2.5 }}>
                Create your first monitor to start tracking website uptime.
              </Typography>
              <Button
                component={RouterLink}
                to="/monitors/new"
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
              >
                Add Your First Monitor
              </Button>
            </>
          )}
        </Paper>
      ) : (
        <>
          <Paper sx={{ overflow: 'hidden' }}>
            <TableHeader />
            {paginatedMonitors.map((monitor) => (
              <MonitorRow
                key={monitor.id}
                monitor={{
                  id: monitor.id,
                  name: monitor.name,
                  url: monitor.url,
                  type: monitor.type as 'HTTP' | 'PING' | 'TCP' | 'KEYWORD',
                  status: monitor.status as MonitorStatus,
                  uptimePercentage: monitor.uptimePercentage,
                  avgResponseTime: monitor.avgResponseTime,
                  lastCheckedAt: monitor.lastCheckedAt,
                  recentChecks: monitor.recentChecks,
                }}
                onPause={handlePause}
                onResume={handleResume}
                onDelete={handleDeleteClick}
              />
            ))}
          </Paper>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 3,
              }}
            >
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                size={isMobile ? 'small' : 'medium'}
              />
            </Box>
          )}

          {/* Results summary */}
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1.5 }}>
            Showing {(currentPage - 1) * PAGE_SIZE + 1}-
            {Math.min(currentPage * PAGE_SIZE, filteredMonitors.length)} of{' '}
            {filteredMonitors.length} monitors
          </Typography>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Monitor</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this monitor? This action cannot be undone and will
            permanently delete all associated check history and incidents.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MonitorsPage;
